import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth, api } from '../../contexts/AuthContext';
import { useTheme, getAllThemes, getThemeDisplayName } from '../../contexts/ThemeContext';
import Button from '../ui/Button';
import Input from '../ui/Input';

const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;

const AccountSettings: React.FC = () => {
  const { user, logout, updateProfile, token } = useAuth();
  const isDemoUser = user?.username === 'Demo';
  const { theme, setTheme } = useTheme();
  const [isPublic, setIsPublic] = useState(false);
  const [statsPrivacy, setStatsPrivacy] = useState({
    public_stats_overview: true,
    public_stats_insights: true,
    public_stats_ratings: true,
    public_stats_seasonal: true,
    public_stats_houses: true,
    public_stats_concentrations: true,
    public_stats_notes: true
  });
  
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'preferences'>('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Profile update form
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
    password: ''
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  
  // Availability checking state
  const [availabilityStatus, setAvailabilityStatus] = useState<Record<string, 'checking' | 'available' | 'taken' | 'error' | undefined>>({});
  const usernameTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [deleteAccountForm, setDeleteAccountForm] = useState({
    password: '',
    confirmDelete: ''
  });

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (usernameTimeoutRef.current) {
        clearTimeout(usernameTimeoutRef.current);
      }
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
    };
  }, []);

  // Availability checking functions
  const checkUsernameAvailability = async (username: string) => {
    if (!username.trim() || username.length < 3 || username.trim() === user?.username) return;
    
    try {
      setAvailabilityStatus(prev => ({ ...prev, username: 'checking' }));
      
      const response = await axios.get(`${API_BASE_URL}/auth/check-username?username=${encodeURIComponent(username)}`);
      
      if (response.data.success) {
        setAvailabilityStatus(prev => ({ 
          ...prev, 
          username: response.data.available ? 'available' : 'taken' 
        }));
        
        if (!response.data.available) {
          setFieldErrors(prev => ({ ...prev, username: response.data.message }));
        }
      } else {
        setAvailabilityStatus(prev => ({ ...prev, username: 'error' }));
      }
    } catch (error: any) {
      console.error('Username availability check error:', error);
      
      if (error.response?.status === 429) {
        setFieldErrors(prev => ({ ...prev, username: 'Please slow down. Try again in a moment.' }));
        setAvailabilityStatus(prev => ({ ...prev, username: 'error' }));
      } else {
        setAvailabilityStatus(prev => ({ ...prev, username: 'error' }));
      }
    }
  };

  const checkEmailAvailability = async (email: string) => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.trim() === user?.email) return;
    
    try {
      setAvailabilityStatus(prev => ({ ...prev, email: 'checking' }));
      
      const response = await axios.get(`${API_BASE_URL}/auth/check-email?email=${encodeURIComponent(email)}`);
      
      if (response.data.success) {
        setAvailabilityStatus(prev => ({ 
          ...prev, 
          email: response.data.available ? 'available' : 'taken' 
        }));
        
        if (!response.data.available) {
          setFieldErrors(prev => ({ ...prev, email: response.data.message }));
        }
      } else {
        setAvailabilityStatus(prev => ({ ...prev, email: 'error' }));
      }
    } catch (error: any) {
      console.error('Email availability check error:', error);
      
      if (error.response?.status === 429) {
        setFieldErrors(prev => ({ ...prev, email: 'Please slow down. Try again in a moment.' }));
        setAvailabilityStatus(prev => ({ ...prev, email: 'error' }));
      } else {
        setAvailabilityStatus(prev => ({ ...prev, email: 'error' }));
      }
    }
  };

  // Real-time field validation
  const validateField = (field: 'username' | 'email', value: string): string => {
    const trimmedValue = value.trim();
    const currentValue = user?.[field];

    if (!trimmedValue) {
      return '';
    }

    if (trimmedValue.toLowerCase() === currentValue?.toLowerCase()) {
      return `This is already your current ${field}`;
    }

    if (field === 'username') {
      if (trimmedValue.length < 3) {
        return 'Username must be at least 3 characters long';
      }
      if (trimmedValue.length > 20) {
        return 'Username must be no more than 20 characters long';
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(trimmedValue)) {
        return 'Username can only contain letters, numbers, underscores, and hyphens';
      }
    }

    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedValue)) {
        return 'Please enter a valid email address';
      }
    }

    return '';
  };

  // Password strength validation
  const validatePassword = (password: string): string => {
    if (!password) return '';
    
    const errors = [];
    
    if (password.length < 8) {
      errors.push('at least 8 characters');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('one lowercase letter');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('one uppercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('one number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('one special character');
    }
    
    if (errors.length > 0) {
      return `Password must contain ${errors.join(', ')}`;
    }
    
    return '';
  };

  // Handle input changes with real-time validation and availability checking
  const handleProfileInputChange = (field: 'username' | 'email' | 'password', value: string) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
    if (user?.username === 'Demo') return;
    
    // Clear general message when user starts typing
    if (message) {
      setMessage(null);
    }

    // Validate field in real-time (except password)
    if (field !== 'password') {
      const error = validateField(field, value);
      setFieldErrors(prev => ({ ...prev, [field]: error }));

      // Clear availability status when user starts typing
      setAvailabilityStatus(prev => ({ ...prev, [field]: undefined }));

      // Debounced availability check
      if (field === 'username') {
        if (usernameTimeoutRef.current) {
          clearTimeout(usernameTimeoutRef.current);
        }
        
        if (value.trim().length >= 3 && !error && value.trim() !== user?.username) {
          usernameTimeoutRef.current = setTimeout(() => {
            checkUsernameAvailability(value.trim());
          }, 800); // 800ms debounce
        }
      } else if (field === 'email') {
        if (emailTimeoutRef.current) {
          clearTimeout(emailTimeoutRef.current);
        }
        
        if (value.trim() && !error && value.trim() !== user?.email) {
          emailTimeoutRef.current = setTimeout(() => {
            checkEmailAvailability(value.trim());
          }, 800); // 800ms debounce
        }
      }
    } else {
      // Clear password field error when user starts typing
      setFieldErrors(prev => ({ ...prev, password: '' }));
    }
  };

  // Handle password form changes with real-time validation
  const handlePasswordInputChange = (field: 'currentPassword' | 'newPassword' | 'confirmPassword', value: string) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    
    if (user?.username === 'Demo') return;
    
    // Clear general message when user starts typing
    if (message) {
      setMessage(null);
    }

    // Validate fields in real-time
    const newErrors = { ...passwordErrors };

    if (!value.trim()) {
      newErrors[field] = '';
      setPasswordErrors(newErrors);
      return;
    }

    if (field === 'currentPassword') {
      newErrors.currentPassword = value ? '' : 'Current password is required';
    } else if (field === 'newPassword') {
      newErrors.newPassword = validatePassword(value);
      
      // Also validate confirm password if it exists
      if (passwordForm.confirmPassword && value !== passwordForm.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else if (passwordForm.confirmPassword && value === passwordForm.confirmPassword) {
        newErrors.confirmPassword = '';
      }
    } else if (field === 'confirmPassword') {
      if (!value) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (value !== passwordForm.newPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        newErrors.confirmPassword = '';
      }
    }

    setPasswordErrors(newErrors);
  };

  const handleProfileUpdate = async (field: 'username' | 'email') => {
    try {
      setIsLoading(true);
      setMessage(null);

      // Validate that the new value is different from current value
      const newValue = profileForm[field].trim();
      const currentValue = user?.[field];
      if (user?.username === 'Demo') return;
      
      if (!newValue) {
        setMessage({ type: 'error', text: `${field.charAt(0).toUpperCase() + field.slice(1)} cannot be empty` });
        return;
      }

      if (newValue === currentValue) {
        setMessage({ type: 'error', text: `This is already your current ${field}. Please enter a different ${field}.` });
        return;
      }

      // Additional validation for username
      if (field === 'username') {
        if (newValue.length < 3) {
          setMessage({ type: 'error', text: 'Username must be at least 3 characters long' });
          return;
        }
        if (newValue.length > 20) {
          setMessage({ type: 'error', text: 'Username must be no more than 20 characters long' });
          return;
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(newValue)) {
          setMessage({ type: 'error', text: 'Username can only contain letters, numbers, underscores, and hyphens' });
          return;
        }
      }

      // Additional validation for email
      if (field === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newValue)) {
          setMessage({ type: 'error', text: 'Please enter a valid email address' });
          return;
        }
      }

      if (!profileForm.password) {
        setMessage({ type: 'error', text: 'Password is required to update your profile' });
        return;
      }

      const endpoint = field === 'username' ? '/auth/username' : '/auth/email';
      const data = {
        [field]: newValue,
        password: profileForm.password
      };

      const response = await api.put(endpoint, data);

      if (response.data.success) {
        if (field === 'email' && response.data.emailChangeRequested) {
          // Email change requires confirmation via email when EMAIL_ENABLED=true
          setMessage({ 
            type: 'success', 
            text: response.data.message || 'Email change confirmation sent! Please check your email and click the confirmation link to complete the change.' 
          });
          setProfileForm(prev => ({ ...prev, password: '' }));
        } else {
          // Direct update (EMAIL_ENABLED=false or username change)
          updateProfile({ [field]: profileForm[field] });
          setMessage({ type: 'success', text: response.data.message || `${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully` });
          setProfileForm(prev => ({ ...prev, password: '' }));
          
          // Update token if username changed
          if (field === 'username' && response.data.data?.token) {
            localStorage.setItem('fragrance-organizer-token', response.data.data.token);
          }
        }
      } else {
        setMessage({ type: 'error', text: response.data.message || `Failed to update ${field}` });
      }
    } catch (error: any) {
      console.error(`Update ${field} error:`, error);
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        const errorMessage = error.response.data.message;
        if (errorMessage?.includes('already exists')) {
          setMessage({ 
            type: 'error', 
            text: field === 'username' ? 'This username is already taken. Please choose a different one.' : 'This email address is already in use. Please use a different email.' 
          });
        } else if (errorMessage?.includes('Password is incorrect')) {
          setMessage({ type: 'error', text: 'The password you entered is incorrect. Please try again.' });
        } else {
          setMessage({ type: 'error', text: errorMessage || `Failed to update ${field}` });
        }
      } else if (error.response?.status === 409) {
        // Conflict - duplicate username or email
        setMessage({ 
          type: 'error', 
          text: field === 'username' ? 'This username is already taken. Please choose a different one.' : 'This email address is already in use. Please use a different email.' 
        });
      } else {
        setMessage({ type: 'error', text: error.response?.data?.message || `Failed to update ${field}. Please try again.` });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setIsLoading(true);
      setMessage(null);

      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setMessage({ type: 'error', text: 'New passwords do not match' });
        return;
      }

      const response = await api.put('/auth/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to change password' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to change password' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsLoading(true);
      setMessage(null);

      if (deleteAccountForm.confirmDelete !== 'DELETE') {
        setMessage({ type: 'error', text: 'Please type DELETE to confirm account deletion' });
        return;
      }

      const response = await api.delete('/auth/account', {
        data: { password: deleteAccountForm.password }
      });

      if (response.data.success) {
        if (response.data.immediate) {
          // Account deleted immediately - logout and redirect
          setMessage({ type: 'success', text: response.data.message });
          setTimeout(() => {
            logout();
          }, 2000);
        } else {
          // Email confirmation required
          setMessage({ 
            type: 'success', 
            text: response.data.message 
          });
          setDeleteAccountForm({ password: '', confirmDelete: '' });
        }
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to delete account' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete account' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleThemeChange = async (newTheme: string) => {
    try {
      setTheme(newTheme as any);
      
      // Update theme preference on server
      await api.put('/auth/preferences', { theme: newTheme });
      
      setMessage({ type: 'success', text: 'Theme updated successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update theme preference:', error);
      // Theme still changes locally even if server update fails
    }
  };

  const handleSharingChange = async (newIsPublic: boolean) => {
    try {
      setIsPublic(newIsPublic);
      
      // Update sharing preference on server
      await api.put('/auth/preferences', { is_public: newIsPublic });
      
      setMessage({ 
        type: 'success', 
        text: `Collection is now ${newIsPublic ? 'public' : 'private'}` 
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update sharing preference:', error);
      setMessage({ type: 'error', text: 'Failed to update sharing preference' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleStatsPrivacyChange = async (key: string, value: boolean) => {
    try {
      const newStatsPrivacy = { ...statsPrivacy, [key]: value };
      setStatsPrivacy(newStatsPrivacy);
      
      // Update statistics privacy on server
      await api.put('/auth/preferences', newStatsPrivacy);
      
      setMessage({ 
        type: 'success', 
        text: `Statistics privacy updated` 
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update statistics privacy:', error);
      setMessage({ type: 'error', text: 'Failed to update statistics privacy' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // Load user preferences on mount
  useEffect(() => {
    if (!token) return;
    const loadPreferences = async () => {
      try {
        const response = await api.get('/auth/preferences');
        if (response.data.success) {
          const prefs = response.data.data.preferences;
          setIsPublic(prefs.is_public || false);
          setStatsPrivacy({
            public_stats_overview: prefs.public_stats_overview ?? true,
            public_stats_insights: prefs.public_stats_insights ?? true,
            public_stats_ratings: prefs.public_stats_ratings ?? true,
            public_stats_seasonal: prefs.public_stats_seasonal ?? true,
            public_stats_houses: prefs.public_stats_houses ?? true,
            public_stats_concentrations: prefs.public_stats_concentrations ?? true,
            public_stats_notes: prefs.public_stats_notes ?? true
          });
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
      }
    };
    loadPreferences();
  }, [token]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Account Settings
        </h1>
      </div>

      {/* Message Display */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'
          }`}
        >
          {message.text}
        </motion.div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-8 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {[
          { id: 'profile', label: 'Profile' },
          { id: 'security', label: 'Security' },
          { id: 'preferences', label: 'Preferences' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        {activeTab === 'profile' && (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Profile Information
              </h3>
              
              {/* Username Update */}
              <div className="space-y-4 mb-8">
                <div className="relative">
                  <Input
                    label="Username"
                    value={profileForm.username}
                    onChange={(e) => handleProfileInputChange('username', e.target.value)}
                    placeholder={
                      isDemoUser ? 'Unavailable in Demo' : user?.username
                    }
                    disabled={isLoading || isDemoUser}
                    error={fieldErrors.username}
                    helperText={
                      isDemoUser ? 'Not available on demo account' :
                      availabilityStatus.username === 'checking' ? '🔄 Checking availability...' :
                      availabilityStatus.username === 'available' ? '✅ Username is available' :
                      availabilityStatus.username === 'taken' ? '❌ Username is already taken' :
                      '3-20 characters, letters, numbers, underscores, and hyphens only'
                    }
                  />
                  {availabilityStatus.username === 'checking' && (
                    <div className="absolute right-3 top-8 text-blue-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                    </div>
                  )}
                </div>
                {/* Email Update */}
                <div className="relative">
                  <Input
                    label="Email Address"
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => handleProfileInputChange('email', e.target.value)}
                    placeholder={
                      isDemoUser ? 'Unavailable in Demo' : user?.email
                    }
                    disabled={isLoading || isDemoUser}
                    error={fieldErrors.email}
                    helperText={
                      isDemoUser ? 'Not available on demo account' :
                      availabilityStatus.email === 'checking' ? '🔄 Checking availability...' :
                      availabilityStatus.email === 'available' ? '✅ Email is available' :
                      availabilityStatus.email === 'taken' ? '❌ Email is already in use' :
                      undefined
                    }
                  />
                  {availabilityStatus.email === 'checking' && (
                    <div className="absolute right-3 top-8 text-blue-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                    </div>
                  )}
                </div>
                <Input
                  label={
                    <>
                      Password{" "}
                      <span className="text-xs italic text-gray-400">(required)</span>
                    </>
                  }
                  type="password"
                  value={profileForm.password}
                  onChange={(e) => handleProfileInputChange('password', e.target.value)}
                  disabled={isLoading || isDemoUser}
                  error={fieldErrors.password}
                  helperText={isDemoUser ? 'Unavailable in Demo' : "Enter your current password to confirm changes"}
                />
                <div className="flex space-x-4">
                  <Button
                    onClick={() => handleProfileUpdate('username')}
                    disabled={
                      isLoading || 
                      isDemoUser ||
                      !profileForm.username || 
                      !profileForm.password || 
                      !!fieldErrors.username ||
                      profileForm.username.trim() === user?.username ||
                      availabilityStatus.username === 'checking' ||
                      availabilityStatus.username === 'taken'
                    }
                    isLoading={isLoading}
                  >
                    Update Username
                  </Button>
                  <Button
                    onClick={() => handleProfileUpdate('email')}
                    disabled={
                      isLoading || 
                      isDemoUser ||
                      !profileForm.email || 
                      !profileForm.password || 
                      !!fieldErrors.email ||
                      profileForm.email.trim() === user?.email ||
                      availabilityStatus.email === 'checking' ||
                      availabilityStatus.email === 'taken'
                    }
                    isLoading={isLoading}
                  >
                    Update Email
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-8">
            {/* Password Change */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Change Password
              </h3>
              <div className="space-y-4 mb-8">
                <Input
                  label="Current Password"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                  disabled={isLoading || isDemoUser}
                  error={passwordErrors.currentPassword}
                  helperText={isDemoUser ? 'Unavailable in Demo' : undefined}
                />
                <Input
                  label="New Password"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                  disabled={isLoading || isDemoUser}
                  error={passwordErrors.newPassword}
                  helperText={isDemoUser ? 'Unavailable in Demo' : "8+ characters with uppercase, lowercase, number, and special character"}
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                  disabled={isLoading || isDemoUser}
                  error={passwordErrors.confirmPassword}
                  helperText={isDemoUser ? 'Unavailable in Demo' : undefined}
                />
                <Button
                  onClick={handlePasswordChange}
                  disabled={
                    isLoading || 
                    isDemoUser || 
                    !passwordForm.currentPassword || 
                    !passwordForm.newPassword || 
                    !passwordForm.confirmPassword ||
                    !!passwordErrors.currentPassword ||
                    !!passwordErrors.newPassword ||
                    !!passwordErrors.confirmPassword
                  }
                  isLoading={isLoading}
                >
                  Change Password
                </Button>
              </div>
            </div>

            {/* Delete Account */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
                Delete Account
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                This action cannot be undone. All your fragrances and data will be permanently deleted.
              </p>
              <div className="space-y-4">
                <Input
                  label="Password"
                  type="password"
                  value={deleteAccountForm.password}
                  onChange={(e) => setDeleteAccountForm(prev => ({ ...prev, password: e.target.value }))}
                  disabled={isLoading || isDemoUser}
                  helperText={isDemoUser ? 'Unavailable in Demo' : undefined}
                />
                <Input
                  label='Type "DELETE" to confirm'
                  value={deleteAccountForm.confirmDelete}
                  onChange={(e) => setDeleteAccountForm(prev => ({ ...prev, confirmDelete: e.target.value }))}
                  placeholder={isDemoUser ? 'Unavailable in Demo' : undefined}
                  disabled={isLoading || isDemoUser}
                />
                <Button
                  onClick={handleDeleteAccount}
                  variant="destructive"
                  disabled={isLoading || isDemoUser || !deleteAccountForm.password || deleteAccountForm.confirmDelete !== 'DELETE'}
                  isLoading={isLoading}
                >
                  Delete Account Permanently
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Collection Sharing
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => handleSharingChange(false)}
                  className={`p-4 rounded-lg border-2 transition-colors duration-200 ${
                    !isPublic
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    🔒 Private
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Only you can see your collection
                  </div>
                  {!isPublic && (
                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Currently active
                    </div>
                  )}
                </button>
                
                <button
                  onClick={() => handleSharingChange(true)}
                  className={`p-4 rounded-lg border-2 transition-colors duration-200 ${
                    isPublic
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    🌐 Public
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Others can view your collection
                  </div>
                  {isPublic && (
                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Currently active
                    </div>
                  )}
                </button>
              </div>
              
              {isPublic && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
                    🌐 Your Collection is Public
                  </h4>
                  <p className="text-green-600 dark:text-green-400 text-sm mb-3">
                    Others can view your collection at this URL (personal notes remain private):
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                    <div className="flex-1 bg-white dark:bg-gray-800 border border-green-300 dark:border-green-600 rounded px-3 py-2 text-sm font-mono break-all">
                      {window.location.origin}/collection/{user?.username}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        const url = `${window.location.origin}/collection/${user?.username}`;
                        
                        // Check if navigator.clipboard is available (HTTPS or localhost)
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(url).then(() => {
                            setMessage({ type: 'success', text: 'Collection URL copied to clipboard!' });
                            setTimeout(() => setMessage(null), 3000);
                          }).catch(() => {
                            setMessage({ type: 'error', text: 'Failed to copy URL' });
                            setTimeout(() => setMessage(null), 3000);
                          });
                        } else {
                          // Fallback for older browsers or HTTP
                          try {
                            const textArea = document.createElement('textarea');
                            textArea.value = url;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            textArea.style.top = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();
                            document.execCommand('copy');
                            textArea.remove();
                            setMessage({ type: 'success', text: 'Collection URL copied to clipboard!' });
                            setTimeout(() => setMessage(null), 3000);
                          } catch (err) {
                            setMessage({ type: 'error', text: 'Failed to copy URL. Please copy manually.' });
                            setTimeout(() => setMessage(null), 3000);
                          }
                        }
                      }}
                      className="flex-shrink-0"
                    >
                      <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="hidden sm:inline">Copy</span>
                    </Button>
                  </div>
                </div>
              )}
              
              {!isPublic && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    🔒 Your collection is private and only visible to you
                  </p>
                </div>
              )}
            </div>

            {/* Statistics Privacy Settings - Only show if collection is public */}
            {isPublic && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Statistics Privacy Settings
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  Choose which statistics sections are visible in your public collection:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'public_stats_overview', label: 'Overview Cards', description: 'Total fragrances, decants, favorites, houses' },
                    { key: 'public_stats_insights', label: 'Collection Insights', description: 'Profile analysis and rating insights' },
                    { key: 'public_stats_ratings', label: 'Average Ratings', description: 'Overall, longevity, and sillage averages' },
                    { key: 'public_stats_seasonal', label: 'Seasonal Preferences', description: 'Winter, spring, summer, fall ratings' },
                    { key: 'public_stats_houses', label: 'Top Houses', description: 'Most collected fragrance houses' },
                    { key: 'public_stats_concentrations', label: 'Concentration Types', description: 'EdT, EdP, Parfum breakdown' },
                    { key: 'public_stats_notes', label: 'Most Common Notes', description: 'Frequently used fragrance notes' }
                  ].map((setting) => (
                    <button
                      key={setting.key}
                      onClick={() => handleStatsPrivacyChange(setting.key, !statsPrivacy[setting.key as keyof typeof statsPrivacy])}
                      className={`p-4 rounded-lg border-2 transition-colors duration-200 text-left ${
                        statsPrivacy[setting.key as keyof typeof statsPrivacy]
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {statsPrivacy[setting.key as keyof typeof statsPrivacy] ? '👁️' : '🔒'} {setting.label}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded ${
                          statsPrivacy[setting.key as keyof typeof statsPrivacy] 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                            : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {statsPrivacy[setting.key as keyof typeof statsPrivacy] ? 'Visible' : 'Hidden'}
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {setting.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Theme Preferences
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getAllThemes().map((themeOption) => (
                  <button
                    key={themeOption}
                    onClick={() => handleThemeChange(themeOption)}
                    className={`p-4 rounded-lg border-2 transition-colors duration-200 ${
                      theme === themeOption
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {getThemeDisplayName(themeOption)}
                    </div>
                    {theme === themeOption && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        Currently active
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Account Information */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Account Information
              </h3>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Username:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{user?.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Email:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{user?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Email Verified:</span>
                  <span className={`font-medium ${user?.email_verified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {user?.email_verified ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Member Since:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;
